import { __awaiter } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { customerQueries, customerMutations, customerQueryKeys } from '@as-react/common-queries';
import { PreferenceTypes, } from '@as-react/common-types';
import { cloneDeep } from '@as-react/common-utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AccountPreferences } from '../../components/account/account-preferences/AccountPreferences';
import { DELAY } from '../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { populatePreferencesWithSubCategories } from '../../util/AccountUtil';
const AccountPreferencesContainer = ({ items, topText, bottomText, pathPrivacy, pathTerms, title, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.account);
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    // Only track local changes in memory, not full state copies
    const [preferences, setPreferences] = useState();
    const [multiFasciaPreferences, setMultiFasciaPreferences] = useState();
    const [preferenceList, setPreferenceList] = useState();
    const [submitButtonState, setSubmitButtonState] = useState('interactive');
    const [errorFeedback, setErrorFeedback] = useState('');
    const [successFeedback, setSuccessFeedback] = useState();
    const { getPreferences, getAllPreferences } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const { updatePreferencesMutation, updateMultiFasciaPreferencesMutation } = customerMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const isUKMarket = defaultRequestParams.market === 'uk';
    // Fetch initial data
    const { data: customerPreferences } = useQuery(getPreferences({
        enabled: !isUKMarket,
    }));
    const { data: customerPreferencesAll } = useQuery(getAllPreferences({
        enabled: isUKMarket,
        select: data => {
            data.fascias.sort(({ sortOrder: a }, { sortOrder: b }) => a - b);
            return data;
        },
    }));
    // Initialize local state from API data
    useEffect(() => {
        if (isUKMarket && customerPreferencesAll) {
            setMultiFasciaPreferences(cloneDeep(customerPreferencesAll));
        }
        else if (!isUKMarket && customerPreferences) {
            const preferencesTmp = customerPreferences.reduce((acc, cur) => (Object.assign(Object.assign({}, acc), { [cur.code]: cur.isSubscribed })), {});
            setPreferenceList(customerPreferences);
            setPreferences(preferencesTmp);
        }
    }, [customerPreferences, customerPreferencesAll, isUKMarket]);
    // Mutations with success handlers
    const updatePreferencesMut = useMutation(updatePreferencesMutation({
        onSuccess: () => {
            handleUpdateSuccess();
            queryClient.invalidateQueries(getPreferences());
        },
        onError: () => {
            setSubmitButtonState('interactive');
            setErrorFeedback(t('account.fail'));
        },
    }));
    const updateMultiFasciaPreferencesMut = useMutation(updateMultiFasciaPreferencesMutation({
        onSuccess: () => {
            handleUpdateSuccess();
            queryClient.invalidateQueries(getAllPreferences());
        },
        onError: () => {
            setSubmitButtonState('interactive');
            setErrorFeedback(t('account.fail'));
        },
    }));
    const handleUpdateSuccess = () => {
        setSubmitButtonState('interactive');
        setSuccessFeedback(t('account:account.preferences.update.success'));
        setTimeout(() => {
            setSuccessFeedback(undefined);
            setSubmitButtonState('interactive');
        }, DELAY.DELAY_3000);
    };
    const analytics = (currentPreferences, updatedPreferences) => {
        let subscriptions = {};
        let unsubscriptions = {};
        Object.keys(updatedPreferences).map(updatedKey => {
            Object.keys(currentPreferences).map(currentKey => {
                if (currentKey === updatedKey &&
                    updatedPreferences[updatedKey] !==
                        currentPreferences[currentKey]) {
                    if (currentPreferences[currentKey] === false &&
                        updatedPreferences[updatedKey] === true) {
                        subscriptions = Object.assign(Object.assign({}, subscriptions), { [updatedKey]: updatedPreferences[updatedKey] });
                    }
                    else {
                        unsubscriptions = Object.assign(Object.assign({}, unsubscriptions), { [updatedKey]: updatedPreferences[updatedKey] });
                    }
                }
            });
        });
        const hasSubscriptions = Boolean(Object.keys(subscriptions).length);
        const hasUnsubscriptions = Boolean(Object.keys(unsubscriptions).length);
        if (hasSubscriptions && hasUnsubscriptions) {
            dispatch(analyticsActions.newsletterPreferencesSubscriptionUpdate({ subscriptions, unsubscriptions }));
        }
        else if (hasSubscriptions) {
            dispatch(analyticsActions.newsletterPreferencesSubscribe(subscriptions));
        }
        else if (hasUnsubscriptions) {
            dispatch(analyticsActions.newsletterPreferencesUnsubscribe(unsubscriptions));
        }
        dispatch(analyticsActions.newsletterPreferencesUpdate(updatedPreferences));
    };
    const updatePreferences = () => __awaiter(void 0, void 0, void 0, function* () {
        setSubmitButtonState('loading');
        setErrorFeedback('');
        try {
            if (isUKMarket) {
                if (!multiFasciaPreferences)
                    return;
                // Get the current data from cache for analytics
                const cachedData = queryClient.getQueryData(customerQueryKeys.getAllPreferences());
                // Send mutation
                yield updateMultiFasciaPreferencesMut.mutateAsync(multiFasciaPreferences);
                // Trigger analytics with original data
                if (cachedData) {
                    analytics(cachedData, multiFasciaPreferences);
                }
            }
            else {
                if (!preferences || !preferenceList)
                    return;
                // Get the current data from cache for analytics
                const cachedData = queryClient.getQueryData(customerQueryKeys.getPreferences());
                const cachedPreferences = cachedData === null || cachedData === void 0 ? void 0 : cachedData.reduce((acc, cur) => (Object.assign(Object.assign({}, acc), { [cur.code]: cur.isSubscribed })), {});
                // Send mutation
                yield updatePreferencesMut.mutateAsync(populatePreferencesWithSubCategories(preferenceList));
                // Trigger analytics with original data
                if (cachedPreferences) {
                    analytics(cachedPreferences, preferences);
                }
            }
        }
        catch (_a) {
            setSubmitButtonState('interactive');
            setErrorFeedback(t('account.fail'));
        }
    });
    const onPreferenceChange = (preference, value, fasciaId) => {
        if (isUKMarket) {
            if (!multiFasciaPreferences)
                return;
            if (preference === PreferenceTypes.ETICKET) {
                multiFasciaPreferences.eticket = value;
            }
            else {
                const fascia = multiFasciaPreferences.fascias.find(item => item.fasciaId === fasciaId);
                if (fascia) {
                    fascia.preferences[preference] = value;
                }
            }
            setMultiFasciaPreferences(Object.assign({}, multiFasciaPreferences));
        }
        else {
            if (!preferenceList || !preferences)
                return;
            const preferenceItem = preferenceList.find(item => item.code === preference);
            preferenceList
                .filter(pref => pref.parentPreferenceFlagCode === (preferenceItem === null || preferenceItem === void 0 ? void 0 : preferenceItem.code))
                .forEach(pref => (preferences[pref.code] = value));
            if (preferenceItem)
                preferenceItem.isSubscribed = value;
            preferences[preference] = value;
            const preferenceSubCats = preferenceList.filter(pref => pref.parentPreferenceFlagCode && pref.parentPreferenceFlagCode === (preferenceItem === null || preferenceItem === void 0 ? void 0 : preferenceItem.parentPreferenceFlagCode));
            const allInOneState = preferenceSubCats.every(pref => preferences[pref.code] === value);
            const atLeastOneSubscribed = preferenceSubCats.some(pref => preferences[pref.code] === true);
            const parentPreferenceItem = preferenceList.find(item => item.code === (preferenceItem === null || preferenceItem === void 0 ? void 0 : preferenceItem.parentPreferenceFlagCode));
            if (allInOneState && parentPreferenceItem)
                preferences[parentPreferenceItem.code] = value;
            if (atLeastOneSubscribed && parentPreferenceItem)
                preferences[parentPreferenceItem.code] = true;
            const preferenceListUpdated = preferenceList.map(item => (Object.assign(Object.assign({}, item), { isSubscribed: preferences[item.code] })));
            setPreferenceList(preferenceListUpdated);
            setPreferences(preferences);
        }
    };
    return multiFasciaPreferences || preferences ? (_jsx(AccountPreferences, { isUKMarket: isUKMarket, preferenceItemsConfig: items, preferences: preferences, multiFasciaPreferences: multiFasciaPreferences, onPreferenceChange: onPreferenceChange, updatePreferences: updatePreferences, submitButtonState: submitButtonState, topText: topText, bottomText: bottomText, errorFeedback: errorFeedback, successFeedback: successFeedback, pathPrivacy: pathPrivacy, pathTerms: pathTerms, title: title })) : null;
};
export default AccountPreferencesContainer;
